import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { format, parseISO, subMonths } from 'date-fns'
import { Package, CalendarHeart, Inbox, ChevronRight, Users, Building2, Wallet, ClipboardCheck } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { authApi } from '@/api/auth'
import { useMyCaps } from '@/lib/capabilities'
import SectionCard from '@/components/dashboard/SectionCard'
import StatTile, { type Tone } from '@/components/dashboard/StatTile'
import ScrollTileRow from '@/components/dashboard/ScrollTileRow'
import MonthRangePicker, { monthRangeDates } from '@/components/dashboard/MonthRangePicker'
import CheckinTrendChart from '@/components/dashboard/CheckinTrendChart'
import CheckInOutCard from '@/components/dashboard/CheckInOutCard'
import LeaveSummaryCard from '@/components/dashboard/LeaveSummaryCard'
import LiveClock from '@/components/dashboard/LiveClock'
import DetailModal from '@/components/dashboard/DetailModal'
import { EmptyState } from '@/components/dashboard/BarListWidget'
import type { ListWidgetRow } from '@/components/dashboard/ListWidget'
import { getMyAttendanceOverview, type MyAttendanceDay, type MyAttendanceOverview } from '@/api/attendance'
import { getMyBalance, listMyRequests } from '@/api/leaves'
import { listMyApprovals } from '@/api/attendanceApprovals'
import { listHolidays } from '@/api/holidays'
import { listMyInventory } from '@/api/inventory'

const ymd = (d: Date) => format(d, 'yyyy-MM-dd')
const ym = (d: Date) => format(d, 'yyyy-MM')

/** "2h 05m" from minutes; the ResourceInn tiles show durations, not raw minutes. */
function hm(min: number): string {
  if (!min) return '0m'
  const h = Math.floor(min / 60)
  const m = min % 60
  return h ? `${h}h${m ? ` ${String(m).padStart(2, '0')}m` : ''}` : `${m}m`
}

function fmtTime(iso: string | null): string {
  return iso ? format(new Date(iso), 'h:mm a') : '—'
}

const ATTN_KIND_LABEL: Record<string, string> = {
  checkin: 'Timed check-in', checkout: 'Timed check-out', late_checkout: 'Late check-out', checkout_correction: 'Check-out correction',
}

const STATUS_LABEL: Record<MyAttendanceDay['status'], string> = {
  completed: 'Present', overtime: 'Present (overtime)', less_hours: 'Present (short hours)', working: 'Working',
  wfh: 'Work from home', leave: 'On leave', absent: 'Absent', holiday: 'Holiday', off: 'Day off',
}

/** One Attendance Summary tile: its label/value/colour and which days it lists when clicked. */
interface TileDef {
  key: string
  label: string
  tone: Tone
  value: (o: MyAttendanceOverview) => ReactNode
  days: (d: MyAttendanceDay, today: string) => boolean
}

const TILES: TileDef[] = [
  { key: 'shifts', label: 'Shifts', tone: 'green', value: (o) => o.scheduled_working_days,
    days: (d) => !!d.expected_start && d.status !== 'holiday' },
  { key: 'expected', label: 'Expected Minutes', tone: 'pink', value: (o) => hm(o.expected_minutes),
    days: (d) => !!d.expected_minutes },
  { key: 'worked', label: 'Worked Minutes', tone: 'amber', value: (o) => hm(o.worked_minutes),
    days: (d) => d.worked_minutes > 0 },
  { key: 'short', label: 'Short Minutes', tone: 'blue', value: (o) => hm(o.short_minutes),
    days: (d) => (d.short_minutes || 0) > 0 },
  { key: 'present', label: 'Presents', tone: 'green', value: (o) => o.present_days, days: (d) => !!d.checkin_time },
  { key: 'absent', label: 'Absents', tone: 'red', value: (o) => o.absent_days, days: (d) => d.status === 'absent' },
  { key: 'leave', label: 'Leaves', tone: 'gray', value: (o) => o.leave_days, days: (d) => d.status === 'leave' },
  { key: 'wfh', label: 'Work From Home', tone: 'teal', value: (o) => o.wfh_days, days: (d) => d.status === 'wfh' },
  { key: 'missing', label: 'Missings', tone: 'orange', value: (o) => o.missing_days,
    days: (d, today) => d.is_active && d.date < today },
  { key: 'off', label: 'Day Offs', tone: 'indigo', value: (o) => o.off_days, days: (d) => d.status === 'off' },
  { key: 'holiday', label: 'Holidays', tone: 'purple', value: (o) => o.holiday_days, days: (d) => d.status === 'holiday' },
  { key: 'late', label: 'Late Arrivals', tone: 'yellow', value: (o) => o.late_days, days: (d) => d.is_late },
  { key: 'early', label: 'Early Leaves', tone: 'pink', value: (o) => o.early_left_days,
    days: (d) => !d.is_active && d.early_checkout_minutes >= 15 },
]

function dayRow(d: MyAttendanceDay, today: string): ListWidgetRow {
  const missing = d.is_active && d.date < today
  const bits = [missing ? 'Missing check-out' : STATUS_LABEL[d.status] || d.status]
  if (d.checkin_time) bits.push(`In ${fmtTime(d.checkin_time)}`)
  if (d.checkout_time) bits.push(`Out ${fmtTime(d.checkout_time)}`)
  else if (d.is_active && !missing) bits.push('In progress')
  if (d.is_late) bits.push(`${d.late_minutes}m late`)
  if (d.early_checkout_minutes >= 15 && !d.is_active) bits.push(`${d.early_checkout_minutes}m early`)
  if (d.leave_type && d.status === 'leave') bits.push(d.leave_type)
  return {
    id: d.date,
    name: format(parseISO(d.date), 'EEE, d MMM yyyy'),
    subtitle: bits.join(' · '),
    trailing: (d.worked_minutes && !d.is_active) || d.short_minutes
      ? (
        <span className="text-xs text-gray-500 tabular-nums">
          {d.worked_minutes && !d.is_active ? `${hm(d.worked_minutes)}${d.expected_minutes ? ` / ${hm(d.expected_minutes)}` : ''}` : ''}
          {d.short_minutes ? <span className="ml-1.5 font-medium text-red-600">{hm(d.short_minutes)} short</span> : null}
        </span>
      )
      : undefined,
  }
}

/**
 * Employee dashboard (ResourceInn style): my attendance summary tiles, check-in
 * trends, clock in/out, leave summary, clock, and a few personal widgets. Also
 * used as "My Dashboard" for admins/HR, whose landing page is the HR dashboard.
 */
export default function Home() {
  const { user } = useAuthStore()
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const base = `/${orgSlug}`
  const { has } = useMyCaps()
  const now = new Date()
  const today = ymd(now)
  const thisMonth = ym(now)

  // Modules a platform admin can switch off per org (same source as the sidebar).
  // While loading, assume on so the page doesn't flicker.
  const { data: features } = useQuery({
    queryKey: ['features', orgSlug], queryFn: authApi.getFeatures, staleTime: 60_000, enabled: !!user,
  })
  const on = (key: string) => !features || features.includes(key)
  const attendanceOn = on('attendance')
  const leavesOn = on('leaves')
  const inventoryOn = on('inventory')

  const [summaryRange, setSummaryRange] = useState<[string, string]>([thisMonth, thisMonth])
  const [trendRange, setTrendRange] = useState<[string, string]>([ym(subMonths(now, 1)), thisMonth])
  const [openTile, setOpenTile] = useState<TileDef | null>(null)

  const [sStart, sEnd] = monthRangeDates(summaryRange[0], summaryRange[1], today)
  const [tStart, tEnd] = monthRangeDates(trendRange[0], trendRange[1], today)

  const summary = useQuery({
    queryKey: ['attendance', 'me', 'overview', user?.id, sStart, sEnd],
    queryFn: () => getMyAttendanceOverview(sStart, sEnd),
    enabled: !!user?.id && features !== undefined && attendanceOn,
  })
  const trend = useQuery({
    queryKey: ['attendance', 'me', 'overview', user?.id, tStart, tEnd],
    queryFn: () => getMyAttendanceOverview(tStart, tEnd),
    enabled: !!user?.id && features !== undefined && attendanceOn,
  })
  const balance = useQuery({ queryKey: ['leaves', 'balance', user?.id], queryFn: () => getMyBalance(), enabled: !!user?.id && features !== undefined && leavesOn })
  const leaveReqs = useQuery({ queryKey: ['leaves', 'me', user?.id], queryFn: () => listMyRequests(), enabled: !!user?.id && features !== undefined && leavesOn })
  const attnReqs = useQuery({ queryKey: ['attendance-approvals', 'mine', user?.id], queryFn: listMyApprovals, enabled: !!user?.id && features !== undefined && attendanceOn })
  const holidays = useQuery({
    queryKey: ['holidays', now.getFullYear()],
    queryFn: async () => [...await listHolidays(now.getFullYear()), ...await listHolidays(now.getFullYear() + 1)],
  })
  const assets = useQuery({ queryKey: ['inventory', 'mine', user?.id], queryFn: listMyInventory, enabled: !!user?.id && features !== undefined && inventoryOn })

  const pending = [
    ...(leaveReqs.data || []).filter((r) => r.status === 'pending').map((r) => ({
      id: `l-${r.id}`,
      title: `${r.type.toUpperCase() === 'WFH' ? 'WFH' : `${r.type.charAt(0).toUpperCase()}${r.type.slice(1)} leave`}`,
      when: r.start_date === r.end_date ? r.start_date : `${r.start_date} → ${r.end_date}`,
      who: r.current_approver_name,
    })),
    ...(attnReqs.data || []).filter((r) => r.status === 'pending').map((r) => ({
      id: `a-${r.id}`,
      title: ATTN_KIND_LABEL[r.kind as string] || r.kind,
      when: r.date,
      who: null as string | null,
    })),
  ]
  const upcomingHolidays = (holidays.data || []).filter((h) => h.date >= today).sort((a, b) => a.date.localeCompare(b.date)).slice(0, 5)

  const staffLinks = [
    { cap: 'manage_users', to: `${base}/admin/users`, icon: Users, label: 'Employees' },
    { cap: 'manage_attendance', to: `${base}/admin/attendance`, icon: ClipboardCheck, label: 'Attendance' },
    { cap: 'manage_hr', to: `${base}/admin/organization`, icon: Building2, label: 'Organization' },
    { cap: 'manage_payroll', to: `${base}/admin/payroll`, icon: Wallet, label: 'Payroll' },
    { cap: 'manage_inventory', to: `${base}/admin/inventory`, icon: Package, label: 'Inventory' },
  ].filter((l) => has(l.cap))

  const tileDays = openTile && summary.data
    ? summary.data.days.filter((d) => openTile.days(d, today)).map((d) => dayRow(d, today))
    : []

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 truncate">Welcome, {user?.name?.split(' ')[0]}</h1>
          <p className="text-sm text-gray-500">{format(now, 'EEEE, d MMMM yyyy')}</p>
        </div>
      </div>

      {/* Attendance Summary — one scrolling row of tiles */}
      {attendanceOn && <SectionCard
        title="Attendance Summary"
        action={<MonthRangePicker from={summaryRange[0]} to={summaryRange[1]} max={thisMonth} onChange={(f, t) => setSummaryRange([f, t])} />}
      >
        {summary.isError ? (
          <EmptyState label="Couldn't load your attendance summary." />
        ) : (
          <ScrollTileRow>
            {TILES.map((t) => (
              <StatTile
                key={t.key}
                label={t.label}
                value={summary.data ? t.value(summary.data) : '—'}
                tone={t.tone}
                chevron
                onClick={summary.data ? () => setOpenTile(t) : undefined}
                className="min-w-[190px] snap-start shrink-0"
              />
            ))}
          </ScrollTileRow>
        )}
      </SectionCard>}

      {/* My Check In Trends */}
      {attendanceOn && <SectionCard
        title="My Check In Trends"
        action={<MonthRangePicker from={trendRange[0]} to={trendRange[1]} max={thisMonth} onChange={(f, t) => setTrendRange([f, t])} />}
      >
        {trend.isLoading ? <div className="py-16 text-center text-sm text-gray-400">Loading…</div> : <CheckinTrendChart days={trend.data?.days || []} />}
      </SectionCard>}

      {/* Clock in/out · Leave summary · Clock */}
      <div className="grid lg:grid-cols-3 gap-6">
        {attendanceOn && (
          <SectionCard title="Mark Clock In/Clock Out">
            <CheckInOutCard />
          </SectionCard>
        )}
        {leavesOn && (
          <SectionCard title="Leave Summary" action={<Link to={`${base}/leaves`} className="text-xs text-brand hover:underline">View all</Link>}>
            <LeaveSummaryCard balances={balance.data} />
          </SectionCard>
        )}
        <SectionCard title="Clock" bodyClassName="flex items-center justify-center">
          <LiveClock />
        </SectionCard>
      </div>

      {/* Personal widgets */}
      <div className="grid lg:grid-cols-3 gap-6">
        <SectionCard title={<span className="flex items-center gap-2"><CalendarHeart className="h-4 w-4 text-brand" /> Upcoming Holidays</span>}>
          {upcomingHolidays.length === 0 ? <EmptyState label="No upcoming holidays" /> : (
            <ul className="divide-y divide-gray-100">
              {upcomingHolidays.map((h) => (
                <li key={h.id} className="flex items-center justify-between py-2.5 text-sm">
                  <span className="font-medium text-gray-800">{h.name}</span>
                  <span className="text-gray-500">{format(parseISO(h.date), 'EEE, d MMM')}</span>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard
          title={<span className="flex items-center gap-2"><Inbox className="h-4 w-4 text-brand" /> My Pending Requests</span>}
          action={<Link to={`${base}/requests`} className="text-xs text-brand hover:underline">View all</Link>}
        >
          {pending.length === 0 ? <EmptyState label="Nothing waiting for approval" /> : (
            <ul className="divide-y divide-gray-100">
              {pending.slice(0, 6).map((p) => (
                <li key={p.id} className="py-2.5 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-gray-800">{p.title}</span>
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">Pending</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{p.when}{p.who ? ` · waiting for ${p.who}` : ''}</p>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        {inventoryOn && <SectionCard title={<span className="flex items-center gap-2"><Package className="h-4 w-4 text-brand" /> My Assets</span>}>
          {!assets.data || assets.data.length === 0 ? <EmptyState label="No assets assigned" /> : (
            <ul className="divide-y divide-gray-100">
              {assets.data.map((it) => (
                <li key={it.id} className="py-2.5">
                  <p className="text-sm font-medium text-gray-800 truncate">{it.name}</p>
                  <p className="text-xs text-gray-400 capitalize">{it.category}{it.asset_tag ? ` · ${it.asset_tag}` : ''}</p>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>}
      </div>

      {staffLinks.length > 0 && (
        <SectionCard title="Management">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {staffLinks.map(({ to, icon: Icon, label }) => (
              <Link key={to} to={to} className="group flex items-center gap-2 rounded-lg border border-gray-100 px-3 py-2.5 text-sm text-gray-700 hover:border-brand/30 hover:text-brand">
                <Icon className="h-4 w-4" /> <span className="flex-1">{label}</span>
                <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-brand" />
              </Link>
            ))}
          </div>
        </SectionCard>
      )}

      {openTile && (
        <DetailModal
          title={`${openTile.label} · ${format(parseISO(sStart), 'd MMM')} – ${format(parseISO(sEnd), 'd MMM yyyy')}`}
          rows={tileDays}
          onClose={() => setOpenTile(null)}
          emptyLabel="No days in this range"
        />
      )}
    </div>
  )
}
